import { useState } from 'react'
import {
  CATEGORY_SUGGESTIONS,
  MANUFACTURER_SUGGESTIONS,
  USAGE_STATUSES,
  VERIFICATION_OPTIONS,
  isVerifiedStatus,
} from '../data/constants'
import { createId } from '../utils/format'
import { toSpecEditorRows } from '../utils/specs'
import { getStockStatus, getStockStatusLabel } from '../utils/stock'
import { normalizePartProjects } from '../services/projectApi'
import AdditionalSpecsEditor from './AdditionalSpecsEditor.jsx'
import ProjectPicker from './ProjectPicker.jsx'
import StatusBadge from './StatusBadge.jsx'
import UnitSelect from './UnitSelect.jsx'

const EMPTY_FORM = {
  category: '',
  manufacturer: '',
  model: '',
  name: '',
  description: '',
  quantity: 0,
  minStock: 0,
  unit: '개',
  ratedVoltage: '',
  ratedCurrent: '',
  poles: '',
  breakingCapacity: '',
  power: '',
  contactConfiguration: '',
  coilVoltage: '',
  communication: '',
  dimensions: '',
  powerType: '',
  status: 'ACTIVE',
  verificationStatus: 'UNVERIFIED',
  quantityNeedsVerification: false,
  modelNeedsVerification: false,
  inventoryNote: '',
  note: '',
  projects: [],
}

function buildFormState(initialValue) {
  const merged = { ...EMPTY_FORM, ...initialValue }
  merged.minStock = merged.minimumStock ?? merged.minStock ?? 0
  if (merged.memo && !merged.note) merged.note = merged.memo
  merged.verificationStatus = isVerifiedStatus(merged.verificationStatus) ? 'VERIFIED' : 'UNVERIFIED'
  merged.quantityNeedsVerification = Boolean(merged.quantityNeedsVerification)
  merged.modelNeedsVerification = Boolean(merged.modelNeedsVerification)
  const specRows = toSpecEditorRows(merged.additionalSpecs)
  return {
    ...merged,
    projects: normalizePartProjects(merged.projects),
    additionalSpecs: specRows.length ? specRows : [{ id: createId('spec'), key: '', value: '' }],
  }
}

export default function PartForm({ initialValue, submitLabel, onSubmit, onCancel, isNew = false }) {
  const [form, setForm] = useState(() => buildFormState(initialValue))
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  function setField(name, value) {
    setForm((current) => ({ ...current, [name]: value }))
  }

  async function handleSubmit(event) {
    event.preventDefault()
    if (!form.model.trim() && !form.name.trim()) {
      setError('모델명 또는 품명 중 하나는 입력해 주세요.')
      return
    }
    setError('')
    setSaving(true)
    try {
      await onSubmit(form)
    } catch (err) {
      setError(err.message || '권한이 없습니다.')
    } finally {
      setSaving(false)
    }
  }

  const previewStatus = getStockStatus({
    quantity: isNew ? form.quantity : initialValue?.quantity || 0,
    minStock: form.minStock,
    minimumStock: form.minStock,
  })

  return (
    <form className="part-form" onSubmit={handleSubmit}>
      <div className="form-grid">
        <label className="field">
          <span>분류</span>
          <input
            className="input"
            list="category-suggestions"
            value={form.category}
            onChange={(e) => setField('category', e.target.value)}
            placeholder="예: 차단기"
          />
          <datalist id="category-suggestions">
            {CATEGORY_SUGGESTIONS.map((item) => (
              <option key={item} value={item} />
            ))}
          </datalist>
        </label>
        <label className="field">
          <span>제조사</span>
          <input
            className="input"
            list="manufacturer-suggestions"
            value={form.manufacturer}
            onChange={(e) => setField('manufacturer', e.target.value)}
            placeholder="예: ABB (모를 경우 비워 두세요)"
          />
          <datalist id="manufacturer-suggestions">
            {MANUFACTURER_SUGGESTIONS.map((item) => (
              <option key={item} value={item} />
            ))}
          </datalist>
        </label>
        <label className="field field--wide">
          <span>모델명</span>
          <input
            className="input input--model"
            value={form.model}
            onChange={(e) => setField('model', e.target.value)}
            placeholder="예: S803C B50 (모를 경우 비워 두세요)"
          />
        </label>
        <label className="field field--wide">
          <span>품명</span>
          <input
            className="input"
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            placeholder="예: 고성능 소형차단기"
          />
        </label>
        <div className="field field--wide">
          <span>사용 프로젝트</span>
          <ProjectPicker
            multiple
            activeOnly
            value={form.projects}
            onChange={(projects) => setField('projects', projects)}
            placeholder="프로젝트 검색 후 선택"
          />
          <p className="muted extra-specs__hint">일반 재고 품목은 비워 둘 수 있습니다. 직접 입력은 할 수 없습니다.</p>
        </div>
        {isNew ? (
          <label className="field">
            <span>초기수량</span>
            <input
              className="input"
              type="number"
              min="0"
              step="1"
              inputMode="numeric"
              value={form.quantity}
              onChange={(e) => setField('quantity', e.target.value)}
            />
          </label>
        ) : null}
        <label className="field">
          <span>단위</span>
          <UnitSelect value={form.unit} onChange={(unit) => setField('unit', unit)} />
        </label>
        <label className="field">
          <span>최소재고</span>
          <input
            className="input"
            type="number"
            min="0"
            step="1"
            inputMode="numeric"
            value={form.minStock}
            onChange={(e) => setField('minStock', e.target.value)}
          />
        </label>
        <label className="field">
          <span>상태</span>
          <select className="input" value={form.status} onChange={(e) => setField('status', e.target.value)}>
            {USAGE_STATUSES.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>검수</span>
          <select
            className="input"
            value={form.verificationStatus}
            onChange={(e) => setField('verificationStatus', e.target.value)}
          >
            {VERIFICATION_OPTIONS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>수량 확인 필요</span>
          <input
            className="input"
            type="checkbox"
            checked={Boolean(form.quantityNeedsVerification)}
            onChange={(e) => setField('quantityNeedsVerification', e.target.checked)}
          />
        </label>
        <label className="field">
          <span>사양 확인 필요</span>
          <input
            className="input"
            type="checkbox"
            checked={Boolean(form.modelNeedsVerification)}
            onChange={(e) => setField('modelNeedsVerification', e.target.checked)}
          />
        </label>
        <label className="field field--wide">
          <span>설명</span>
          <textarea
            className="input input--area"
            rows={3}
            value={form.description}
            onChange={(e) => setField('description', e.target.value)}
            placeholder="확인된 내용만 입력. 불확실하면 비워 두세요."
          />
        </label>
        <label className="field">
          <span>정격전압</span>
          <input
            className="input"
            value={form.ratedVoltage}
            onChange={(e) => setField('ratedVoltage', e.target.value)}
            placeholder="예: 240/415VAC"
          />
        </label>
        <label className="field">
          <span>정격전류</span>
          <input
            className="input"
            value={form.ratedCurrent}
            onChange={(e) => setField('ratedCurrent', e.target.value)}
            placeholder="예: 50A"
          />
        </label>
        <label className="field">
          <span>극수</span>
          <input
            className="input"
            value={form.poles}
            onChange={(e) => setField('poles', e.target.value)}
            placeholder="예: 3P"
          />
        </label>
        <label className="field">
          <span>차단용량</span>
          <input
            className="input"
            value={form.breakingCapacity}
            onChange={(e) => setField('breakingCapacity', e.target.value)}
            placeholder="예: 25kA"
          />
        </label>
        <label className="field">
          <span>전력</span>
          <input
            className="input"
            value={form.power}
            onChange={(e) => setField('power', e.target.value)}
            placeholder="예: 350W"
          />
        </label>
        <label className="field">
          <span>접점구성</span>
          <input
            className="input"
            value={form.contactConfiguration}
            onChange={(e) => setField('contactConfiguration', e.target.value)}
            placeholder="예: 1NO+1NC"
          />
        </label>
        <label className="field">
          <span>코일전압</span>
          <input
            className="input"
            value={form.coilVoltage}
            onChange={(e) => setField('coilVoltage', e.target.value)}
            placeholder="예: 24VDC"
          />
        </label>
        <label className="field">
          <span>통신방식</span>
          <input
            className="input"
            value={form.communication}
            onChange={(e) => setField('communication', e.target.value)}
            placeholder="예: RS485 / Modbus RTU"
          />
        </label>
        <label className="field">
          <span>치수</span>
          <input
            className="input"
            value={form.dimensions}
            onChange={(e) => setField('dimensions', e.target.value)}
            placeholder="선택 입력"
          />
        </label>
        <label className="field">
          <span>AC / DC</span>
          <input
            className="input"
            value={form.powerType}
            onChange={(e) => setField('powerType', e.target.value)}
            placeholder="예: AC, DC, AC/DC"
          />
        </label>
        <div className="field">
          <span>재고 상태</span>
          <div className="form-preview">
            <StatusBadge status={previewStatus} />
            <span className="muted">{getStockStatusLabel(previewStatus)} (수량·최소재고로 자동 계산)</span>
          </div>
        </div>
        <div className="field field--wide">
          <span>추가 사양</span>
          <p className="muted extra-specs__hint">분류별로 다른 항목을 자유롭게 추가합니다.</p>
          <AdditionalSpecsEditor
            rows={form.additionalSpecs}
            onChange={(rows) => setField('additionalSpecs', rows)}
          />
        </div>
        <label className="field field--wide">
          <span>조사 메모</span>
          <textarea
            className="input input--area"
            rows={3}
            value={form.inventoryNote}
            onChange={(e) => setField('inventoryNote', e.target.value)}
            placeholder="현장조사 원본 메모, 수량/모델 확인 필요 내용"
          />
        </label>
        <label className="field field--wide">
          <span>비고</span>
          <textarea
            className="input input--area"
            rows={3}
            value={form.note}
            onChange={(e) => setField('note', e.target.value)}
          />
        </label>
      </div>

      {error ? <p className="form-error">{error}</p> : null}

      <div className="form-actions">
        <button type="button" className="btn btn--ghost btn--touch" onClick={onCancel}>
          취소
        </button>
        <button type="submit" className="btn btn--primary btn--touch" disabled={saving}>
          {saving ? '저장 중...' : submitLabel}
        </button>
      </div>
    </form>
  )
}
